import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
} from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useRunDetailsTo} from '../../../components/RunDetails/hooks';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {
  RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import {useStatsigGateWorkspaceConsolidation} from '../../../util/rampFeatureFlags';
import {RunRenameScope} from '../types';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';
import {RunCopyIdentifiersSubmenu} from './RunCopyIdentifiersSubmenu';

type RunRowHeaderMenuProps = {
  runName: string;
  onBenchmarkRun: () => void;
  onCopyRunName: () => void;
  onCopyRunPath: () => void;
  onDeleteRun: () => void;
  onMoveRun: () => void;
  onRenameRun: (scope: RunRenameScope) => void;
  onStopRun: () => void;
  canBenchmarkRun?: boolean;
  canStopRun?: boolean;
};

export function RunRowHeaderMenu(props: RunRowHeaderMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const history = useHistory();
  const trackRunMenuWithLocation = (action: RunMenuAction) => {
    trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
  };
  const runUrl = useRunDetailsTo(props.runName);

  return (
    <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
      <RowHeaderMenuTrigger open={menuOpen} setMenuOpen={setMenuOpen} />
      <DropdownPortal>
        <DropdownContent
          align="start"
          sideOffset={8}
          alignOffset={-8}
          style={{zIndex: 1}}>
          {isWorkspaceConsolidationEnabled && (
            <>
              <RowHeaderMenuOption
                icon={<Icon name="code-alt" />}
                label="View run logs"
                onSelect={() => {
                  history.push(runUrl('logs'));
                  trackRunMenuWithLocation('View run details');
                }}
              />
              <DropdownSeparator />
            </>
          )}
          <RowHeaderMenuOption
            icon={<Icon name="pencil-edit" />}
            label={
              <span>
                Rename run for <span className="font-semibold">project</span>
              </span>
            }
            onSelect={() => {
              props.onRenameRun('project');
              trackRunMenuWithLocation('Rename run (project)');
            }}
          />
          <RowHeaderMenuOption
            icon={<Icon name="pencil-edit" />}
            label={
              <span>
                Rename run for{' '}
                <span className="font-semibold">
                  {isReport ? 'panel grid' : 'workspace'}
                </span>
              </span>
            }
            onSelect={() => {
              props.onRenameRun('view-section');
              trackRunMenuWithLocation('Rename run (custom run name)');
            }}
          />
          <RunCopyIdentifiersSubmenu
            onCopyRunName={() => {
              props.onCopyRunName();
              trackRunMenuWithLocation('Copy run name');
            }}
            onCopyRunPath={() => {
              props.onCopyRunPath();
              trackRunMenuWithLocation('Copy run path');
            }}
          />
          <DropdownSeparator />

          <RowHeaderMenuOption
            icon={<Icon name="forward-next" />}
            label="Move to another group"
            onSelect={() => {
              props.onMoveRun();
              trackRunMenuWithLocation('Move run groups');
            }}
          />
          <RowHeaderMenuOption
            icon={<Icon name="delete" />}
            label="Delete run"
            onSelect={() => {
              props.onDeleteRun();
              trackRunMenuWithLocation('Delete run');
            }}
          />
          {props.canBenchmarkRun ? (
            <RowHeaderMenuOption
              icon={<Icon name="benchmark-square" />}
              label="Submit to benchmark"
              onSelect={props.onBenchmarkRun}
            />
          ) : null}
          {props.canStopRun ? (
            <RowHeaderMenuOption
              icon={<Icon name="stop" />}
              label="Stop run"
              onSelect={() => {
                props.onStopRun();
                trackRunMenuWithLocation('Stop run');
              }}
            />
          ) : null}
        </DropdownContent>
      </DropdownPortal>
    </DropdownRoot>
  );
}
